import { ArrowDownLeft, ArrowUpRight, ChevronDown, Landmark, Percent } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../shared/format'
import { exportBankReportCsv, useBankReport, type BankReportRow } from './bankReport'
import { BankLogo } from './components/banks/BankLogo'
import { BankSelect } from './components/banks/BankSelect'
import { ExportMenu } from './components/ExportMenu'
import { Kpi, PeriodSelect, Section } from './components/FinanceBits'
import { downloadReport } from './reports'

const thisMonth = () => new Date().toISOString().slice(0, 7)
const yearStart = () => `${new Date().getFullYear()}-01`
const COST_KEYS = ['commission', 'card', 'loan', 'cashAdvance', 'other'] as const

/**
 * Banka raporu: seçilen aralıkta banka başına giriş/çıkış, bankaya ödenen faiz ve masraflar (kredi ve kart maliyeti borç
 * veren bankaya yazılır), banka hesaplarından geçen gelir/giderler ve kalan kredi borcu.
 */
export function BankReportPage() {
  const { t } = useTranslation()
  const [from, setFrom] = useState(yearStart)
  const [to, setTo] = useState(thisMonth)
  const [bankId, setBankId] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const report = useBankReport(from, to, bankId)
  const rep = report.data
  const tot = rep?.totals
  const amount = (v: string) => (Number(v) ? fmtMoney(v) : '—')
  const header = [
    t('finance.bankReport.bank'),
    t('finance.bankReport.inflow'),
    t('finance.bankReport.outflow'),
    t('finance.bankReport.transferIn'),
    t('finance.bankReport.transferOut'),
    ...COST_KEYS.map((k) => t(`finance.bankReport.costs.${k}`)),
    t('finance.bankReport.costTotal'),
    t('finance.bankReport.loanDebt'),
    t('finance.bankReport.advanceDebt'),
  ]

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.bankReport.title')}
        description={t('finance.bankReport.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PeriodSelect value={from} onChange={setFrom} />
            <span className="text-muted-foreground text-sm">–</span>
            <PeriodSelect value={to} onChange={setTo} />
            <div className="w-52">
              <BankSelect id="br-bank" value={bankId} onChange={setBankId} emptyLabel={t('finance.bankReport.allBanks')} />
            </div>
            <ExportMenu
              disabled={!rep?.banks.length}
              onExcel={() => downloadReport('banks', 'xlsx', { from, to, bank_id: bankId })}
              onCorporatePdf={() => downloadReport('banks', 'pdf', { from, to, bank_id: bankId })}
              onCsv={() => rep && exportBankReportCsv(rep, header)}
            />
          </div>
        }
      />
      {report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : !rep || !tot ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">{t('finance.bankReport.range', { from: fmtDate(rep.from), to: fmtDate(rep.to) })}</p>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Kpi icon={Percent} tone="danger" label={t('finance.bankReport.costTotal')} value={fmtMoney(tot.costs.total)} footer={t('finance.bankReport.costHint')} />
            <Kpi icon={ArrowDownLeft} tone="success" label={t('finance.bankReport.inflow')} value={fmtMoney(tot.inflow)} />
            <Kpi icon={ArrowUpRight} tone="warning" label={t('finance.bankReport.outflow')} value={fmtMoney(tot.outflow)} />
            <Kpi
              icon={Landmark}
              tone="info"
              label={t('finance.bankReport.debt')}
              value={fmtMoney((Number(tot.loanDebt) + Number(tot.advanceDebt)).toFixed(2))}
              footer={t('finance.bankReport.debtHint', { loan: fmtMoney(tot.loanDebt), advance: fmtMoney(tot.advanceDebt) })}
            />
          </div>
          <Section title={t('finance.bankReport.byBank')} icon={Landmark} bodyClassName="p-0">
            {!rep.banks.length ? (
              <EmptyState icon={Landmark} title={t('finance.bankReport.empty')} description={t('finance.bankReport.emptyHint')} />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('finance.bankReport.bank')}</TH>
                      <TH className="text-right">{t('finance.bankReport.inflow')}</TH>
                      <TH className="text-right">{t('finance.bankReport.outflow')}</TH>
                      {COST_KEYS.map((k) => (
                        <TH key={k} className="text-right whitespace-nowrap">
                          {t(`finance.bankReport.costs.${k}`)}
                        </TH>
                      ))}
                      <TH className="text-right">{t('finance.bankReport.costTotal')}</TH>
                      <TH className="text-right">{t('finance.bankReport.loanDebt')}</TH>
                      <TH />
                    </TR>
                  </THead>
                  <TBody>
                    {rep.banks.map((b) => (
                      <Fragment key={b.bankId}>
                        <TR>
                          <TD>
                            <span className="flex items-center gap-2 font-medium whitespace-nowrap">
                              <BankLogo url={b.logoUrl} name={b.name} />
                              {b.name}
                            </span>
                          </TD>
                          <TD className="text-success text-right tabular-nums">{amount(b.inflow)}</TD>
                          <TD className="text-destructive text-right tabular-nums">{amount(b.outflow)}</TD>
                          {COST_KEYS.map((k) => (
                            <TD key={k} className="text-right tabular-nums">
                              {amount(b.costs[k])}
                            </TD>
                          ))}
                          <TD className="text-right font-semibold tabular-nums">{amount(b.costs.total)}</TD>
                          <TD className="text-right tabular-nums">{amount((Number(b.loanDebt) + Number(b.advanceDebt)).toFixed(2))}</TD>
                          <TD>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-expanded={open === b.bankId}
                              aria-label={t('finance.bankReport.details')}
                              onClick={() => setOpen(open === b.bankId ? null : b.bankId)}
                            >
                              <ChevronDown className={cn('transition-transform', open === b.bankId && 'rotate-180')} />
                            </Button>
                          </TD>
                        </TR>
                        {open === b.bankId ? (
                          <TR>
                            <TD colSpan={COST_KEYS.length + 6} className="bg-muted/30">
                              <BankDetail b={b} />
                            </TD>
                          </TR>
                        ) : null}
                      </Fragment>
                    ))}
                    <TR className="border-t-2 font-semibold">
                      <TD>{t('finance.reports.totals')}</TD>
                      <TD className="text-right tabular-nums">{fmtMoney(tot.inflow)}</TD>
                      <TD className="text-right tabular-nums">{fmtMoney(tot.outflow)}</TD>
                      {COST_KEYS.map((k) => (
                        <TD key={k} className="text-right tabular-nums">
                          {fmtMoney(tot.costs[k])}
                        </TD>
                      ))}
                      <TD className="text-right tabular-nums">{fmtMoney(tot.costs.total)}</TD>
                      <TD className="text-right tabular-nums">{fmtMoney((Number(tot.loanDebt) + Number(tot.advanceDebt)).toFixed(2))}</TD>
                      <TD />
                    </TR>
                  </TBody>
                </Table>
              </div>
            )}
          </Section>
        </div>
      )}
    </>
  )
}

/** Bankanın hesaplarından geçen gelir ve giderler (kategoriye göre) ve aktarımlar. */
function BankDetail({ b }: { b: BankReportRow }) {
  const { t } = useTranslation()
  const list = (dir: 'in' | 'out') => b.categories.filter((c) => c.direction === dir)
  return (
    <div className="grid gap-4 py-2 md:grid-cols-3">
      {(['in', 'out'] as const).map((dir) => (
        <div key={dir} className="space-y-1">
          <p className="text-sm font-medium">{t(dir === 'in' ? 'finance.bankReport.incomeByCategory' : 'finance.bankReport.expenseByCategory')}</p>
          {list(dir).length ? (
            <ul className="space-y-0.5 text-sm">
              {list(dir).map((c) => (
                <li key={c.categoryId || c.name} className="flex justify-between gap-4">
                  <span className="text-muted-foreground truncate">{c.name}</span>
                  <span className="tabular-nums">{fmtMoney(c.amount)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-xs">—</p>
          )}
        </div>
      ))}
      <div className="space-y-1 text-sm">
        <p className="font-medium">{t('finance.bankReport.transfers')}</p>
        <p className="flex justify-between">
          <span className="text-muted-foreground">{t('finance.bankReport.transferIn')}</span>
          <span className="tabular-nums">{fmtMoney(b.transferIn)}</span>
        </p>
        <p className="flex justify-between">
          <span className="text-muted-foreground">{t('finance.bankReport.transferOut')}</span>
          <span className="tabular-nums">{fmtMoney(b.transferOut)}</span>
        </p>
        <p className="flex justify-between">
          <span className="text-muted-foreground">{t('finance.bankReport.advanceDebt')}</span>
          <span className="tabular-nums">{fmtMoney(b.advanceDebt)}</span>
        </p>
      </div>
    </div>
  )
}
